import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Sparkles, X, RefreshCw } from 'lucide-react';

// Server-driven "new version available" banner.
//
// Why this exists alongside the SW-update path: iOS PWAs don't reliably fire
// the SW `updatefound` lifecycle for installed home-screen apps, so users
// can stay stuck on a build from weeks ago even though the server has shipped
// a new one. This component is independent of the SW — it polls
// /api/app/version every 60s, compares to a localStorage marker, and shows a
// prominent banner with a "Refresh" button that does the full nuke (unregister
// SWs + clear caches + cache-busting reload).
//
// First-run: when the marker is empty we silently adopt the current server
// version so existing visitors don't see a banner the moment this code lands.
const POLL_MS = 60 * 1000;
const STORAGE_KEY = 'atrium_version_seen';

export default function VersionMismatchBanner() {
  const [latest, setLatest] = useState(null);   // current server version
  const [seen, setSeen] = useState(null);       // last version we acknowledged
  const [refreshing, setRefreshing] = useState(false);
  const pollTimer = useRef(null);

  // First-run: read the marker once.
  useEffect(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      if (v) setSeen(v);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/app/version`, { cache: 'no-store' });
        if (!r.ok) return;
        const d = await r.json();
        const v = d?.version;
        if (cancelled || !v) return;
        setLatest(v);
        // First time we see ANY version → adopt silently so existing visitors
        // don't get an immediate banner on a brand-new install of this code.
        if (!seen) {
          try { localStorage.setItem(STORAGE_KEY, v); } catch (e) { /* ignore */ }
          setSeen(v);
        }
      } catch (e) { /* network blip — try again on next tick */ }
    };
    check();
    pollTimer.current = setInterval(check, POLL_MS);
    const onFocus = () => check();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      cancelled = true;
      if (pollTimer.current) clearInterval(pollTimer.current);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [seen]);

  const applyRefresh = async () => {
    setRefreshing(true);
    try {
      // 1. Unregister every SW so a fresh /sw.js is fetched on next load.
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch (e) { /* ignore */ }
    try {
      // 2. Clear every Cache Storage entry — this is the part that fixes
      //    "stuck on old build" on iOS PWAs where the bundled JS was cached.
      if (typeof caches !== 'undefined') {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch (e) { /* ignore */ }
    // 3. Adopt the new version so the banner doesn't immediately re-appear.
    try { if (latest) localStorage.setItem(STORAGE_KEY, latest); } catch (e) { /* ignore */ }
    // 4. Hard reload with a cache-busting query so the HTML/JS bundle is fresh.
    const sep = window.location.search ? '&' : '?';
    window.location.href = window.location.pathname + window.location.search + `${sep}_v=${Date.now()}` + window.location.hash;
  };

  const dismiss = () => {
    // Snooze: mark this version as seen so the banner stays hidden until
    // the next deploy. We respect the user's choice rather than nag.
    try { if (latest) localStorage.setItem(STORAGE_KEY, latest); } catch (e) { /* ignore */ }
    setSeen(latest);
  };

  const mismatch = !!(latest && seen && latest !== seen);
  if (!mismatch || typeof document === 'undefined') return null;

  return createPortal(
    <div
      data-testid="version-banner"
      className="fixed top-3 left-1/2 -translate-x-1/2 max-w-[94vw] w-auto rounded-full shadow-2xl flex items-center gap-2 pl-3 pr-1.5 py-1.5"
      style={{
        zIndex: 10003,
        backgroundColor: '#1A2E2E',
        border: '1px solid rgba(139,92,246,0.45)',
        animation: 'fade-slide-down 320ms ease-out',
      }}
    >
      <span className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#8B5CF6' }}>
        <Sparkles className="w-3.5 h-3.5 text-white" />
      </span>
      <span className="text-xs font-bold whitespace-nowrap" style={{ fontFamily: 'Manrope', color: '#F4F1EB' }}>
        New version available
      </span>
      <span className="hidden sm:inline text-[10px] font-mono" style={{ fontFamily: 'monospace', color: '#A3D4D4', opacity: 0.7 }}>
        {seen} → {latest}
      </span>
      <button
        data-testid="version-banner-refresh"
        onClick={applyRefresh}
        disabled={refreshing}
        className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold text-white transition-all hover:-translate-y-0.5 disabled:opacity-60"
        style={{ backgroundColor: '#8B5CF6', fontFamily: 'Manrope', boxShadow: '0 2px 8px rgba(139,92,246,0.4)' }}
      >
        <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
        {refreshing ? 'Refreshing…' : 'Refresh'}
      </button>
      <button
        data-testid="version-banner-dismiss"
        onClick={dismiss}
        className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-colors"
        style={{ backgroundColor: 'rgba(163,212,212,0.1)', color: '#A3D4D4' }}
        aria-label="Dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>,
    document.body
  );
}
